import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '../api/client';

export type SystemNotificationType = 'ORDER' | 'INVENTORY' | 'SYSTEM' | 'PAYMENT';

export interface SystemNotification {
    id: string;
    type: SystemNotificationType;
    title: string;
    message: string;
    read: boolean;
    createdAt: string;
    link?: string;
}

interface SystemNotificationState {
    notifications: SystemNotification[];
    unreadCount: number;
    isLoading: boolean;
    fetchNotifications: () => Promise<void>;
    addNotification: (notification: Omit<SystemNotification, 'id' | 'read' | 'createdAt'>) => void;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    removeNotification: (id: string) => Promise<void>;
}

export const useSystemNotificationStore = create<SystemNotificationState>()(
    persist(
        (set) => ({
            notifications: [],
            unreadCount: 0,
            isLoading: false,
            fetchNotifications: async () => {
                set({ isLoading: true });
                try {
                    const response = await apiClient.get<{ notifications: SystemNotification[], unreadCount: number }>('/notifications');
                    // Handle response structure (apiClient might return data directly or wrapped)
                    // Assuming apiClient.get returns the data property of axios response, which is { success, data, message }
                    // But wait, apiClient implementation I saw earlier unwraps 'data' property if it exists.
                    // Let's assume it returns the payload directly if unwrapped correctly.
                    // If the response from backend is { success: true, data: { notifications, unreadCount } }
                    // apiClient returns { notifications, unreadCount }

                    if (response && typeof response === 'object' && 'notifications' in response) {
                        set({
                            notifications: response.notifications,
                            unreadCount: response.unreadCount,
                            isLoading: false
                        });
                    } else {
                        // Fallback or error
                        set({ isLoading: false });
                    }

                } catch (error) {
                    console.error('Failed to fetch notifications:', error);
                    set({ isLoading: false });
                }
            },
            addNotification: (notification) => {
                const newNotification: SystemNotification = {
                    ...notification,
                    id: Math.random().toString(36).substring(2, 9),
                    read: false,
                    createdAt: new Date().toISOString(),
                };
                set((state) => ({
                    notifications: [newNotification, ...state.notifications],
                    unreadCount: state.unreadCount + 1,
                }));
            },
            markAsRead: async (id) => {
                set((state) => {
                    const notification = state.notifications.find((n) => n.id === id);
                    if (notification && !notification.read) {
                        return {
                            notifications: state.notifications.map((n) =>
                                n.id === id ? { ...n, read: true } : n
                            ),
                            unreadCount: state.unreadCount - 1,
                        };
                    }
                    return state;
                });

                try {
                    await apiClient.patch(`/notifications/${id}/read`);
                } catch (error) {
                    console.error('Failed to mark notification as read:', error);
                }
            },
            markAllAsRead: async () => {
                set((state) => ({
                    notifications: state.notifications.map((n) => ({ ...n, read: true })),
                    unreadCount: 0,
                }));

                try {
                    await apiClient.patch('/notifications/read-all');
                } catch (error) {
                    console.error('Failed to mark all notifications as read:', error);
                }
            },
            removeNotification: async (id) => {
                set((state) => {
                    const notification = state.notifications.find((n) => n.id === id);
                    return {
                        notifications: state.notifications.filter((n) => n.id !== id),
                        unreadCount: notification && !notification.read ? state.unreadCount - 1 : state.unreadCount,
                    };
                });

                try {
                    await apiClient.delete(`/notifications/${id}`);
                } catch (error) {
                    console.error('Failed to delete notification:', error);
                }
            },
        }),
        {
            name: 'system-notifications',
        }
    )
);
